<template>
  <div class="v2-community v2-container v2-section">
    <header class="v2-community__header">
      <div>
        <p class="v2-overline">Membresía</p>
        <h1 class="v2-headline v2-headline--display-lg v2-community__title">
          {{ communityLabel }}
        </h1>
        <p class="v2-body v2-body--md v2-body--muted">
          Consulta las noticias, comunicados e información de tu organización
        </p>
        <p v-if="!loadingPosts && !error" class="v2-body v2-body--sm v2-body--muted v2-community__count">
          {{ sortedPosts.length }} {{ sortedPosts.length === 1 ? "comunicado" : "comunicados" }}
          <span v-if="hasMore">
            · mostrando {{ visiblePosts.length }}
          </span>
        </p>
      </div>
    </header>

    <v-tabs
      v-model="activeSectionTab"
      class="v2-community__sections"
      background-color="transparent"
      color="primary"
      show-arrows
    >
      <v-tab v-for="section in sectionTabs" :key="section._id || 'unsectioned'">
        {{ section.name }}
      </v-tab>
    </v-tabs>

    <div class="v2-community__composer" aria-disabled="true">
      <div class="v2-community__composer-avatar" aria-hidden="true">
        <span>{{ userInitials }}</span>
      </div>
      <div class="v2-community__composer-body">
        <p class="v2-community__composer-prompt">Comparte algo con tu comunidad…</p>
      </div>
      <span class="v2-community__composer-badge">
        <v-icon size="12">mdi-clock-outline</v-icon>
        Próximamente
      </span>
    </div>

    <div v-if="loadingPosts" class="v2-community__loading">
      <v-icon size="32" color="grey lighten-1">mdi-progress-clock</v-icon>
      <p class="v2-body v2-body--sm v2-body--muted">Cargando comunicados…</p>
    </div>

    <div v-else-if="error" class="v2-community__error">
      <EmptyState
        title="No se pudieron cargar los comunicados"
        :description="error"
        icon="mdi-cloud-off-outline"
      >
        <BaseButton variant="primary" icon="mdi-refresh" @click="loadPosts">Reintentar</BaseButton>
      </EmptyState>
    </div>

    <div v-else-if="visiblePosts.length === 0" class="v2-community__empty">
      <EmptyState
        title="Aún no hay comunicados"
        description="Cuando tu cámara publique comunicados aparecerán aquí."
        icon="mdi-message-text-outline"
      />
    </div>

    <template v-else>
      <ul class="v2-community__feed">
        <li
          v-for="post in visiblePosts"
          :key="getPostId(post)"
          class="v2-community__feed-item"
        >
          <PostCard
            :post="post"
            :organization="organization"
            :survey="getSurveyForPost(post)"
            @survey-responded="loadPosts"
          />
        </li>
      </ul>

      <div
        v-if="hasMore"
        ref="sentinel"
        class="v2-community__sentinel"
        aria-hidden="true"
      >
        <v-icon size="20" color="grey lighten-1">mdi-progress-clock</v-icon>
        <span class="v2-body v2-body--sm v2-body--muted">Cargando más comunicados…</span>
      </div>

      <div v-else class="v2-community__end">
        <v-icon size="20" color="grey lighten-1">mdi-check-circle-outline</v-icon>
        <span class="v2-body v2-body--sm v2-body--muted">
          Has visto todos los comunicados ({{ sortedPosts.length }})
        </span>
      </div>
    </template>
  </div>
</template>

<script>
import Vue from "vue";
import { mapState, mapActions, mapMutations } from "vuex";
import BaseButton from "@/components/memberDashboardV2/ui/BaseButton.vue";
import EmptyState from "@/components/memberDashboardV2/ui/EmptyState.vue";
import PostCard from "./PostCard.vue";
import { getCommunityLabel } from "@/share/communityLabel";
import {
  sortPostsForFeed,
      getPostId,
      resolveOrganization,
      getPostSectionId,
} from "./communityHelpers";

const PAGE_SIZE = 6;
const ROOT_MARGIN = "300px";

export default {
  name: "MemberDashboardV2Community",
  components: { BaseButton, EmptyState, PostCard },
  data() {
    return {
      loadingPosts: false,
      error: null,
      allPosts: [],
      allSections: [],
      allSurveys: [],
      activeSectionTab: 0,
      pageCount: 1,
      observer: null,
    };
  },
  computed: {
    ...mapState("user", ["user"]),
    communityLabel() {
      return getCommunityLabel(this.user);
    },
    sortedPosts() {
      const selectedSection = this.sectionTabs[this.activeSectionTab];
      const sectionId = selectedSection && selectedSection._id;
      return sortPostsForFeed(
        this.allPosts.filter((post) => String(getPostSectionId(post) || "") === String(sectionId || "")),
      );
    },
    sectionTabs() {
      return [{ _id: null, name: "Sin sección" }, ...this.allSections];
    },
    visiblePosts() {
      return this.sortedPosts.slice(0, this.pageCount * PAGE_SIZE);
    },
    hasMore() {
      return this.visiblePosts.length < this.sortedPosts.length;
    },
    userInitials() {
      const u = this.user;
      if (!u) return "TÚ";
      const first = (u.name || "").trim().charAt(0);
      const last = (u.firstLastname || "").trim().charAt(0);
      return ((first + last) || "TÚ").toUpperCase();
    },
    organization() {
      return resolveOrganization(this.user);
    },
  },
  watch: {
    activeSectionTab() {
      this.pageCount = 1;
      this.$nextTick(() => this.setupObserver());
    },
    hasMore(shouldObserve) {
      if (shouldObserve) {
        this.$nextTick(() => this.setupObserver());
      } else if (this.observer) {
        this.observer.disconnect();
      }
    },
  },
  methods: {
    ...mapActions("publications", ["fetchMemberPublications"]),
    ...mapActions("publicationSections", ["getPublicationSectionsForMember"]),
    ...mapActions("asocoins", ["getSurveys"]),
    ...mapMutations("loader", ["loading", "loaded"]),
    ...mapMutations("notification", ["show"]),
    getPostId,
    getSurveyForPost(post) {
      const postId = String(getPostId(post) || "");
      return this.allSurveys.find((survey) => String(survey._id) === postId) || null;
    },
    async loadPosts() {
      this.loadingPosts = true;
      this.error = null;
      try {
        const [response, sectionsResponse, surveysResponse] = await Promise.all([
          this.fetchMemberPublications(),
          this.getPublicationSectionsForMember(),
          this.getSurveys({ includeUnavailable: true }),
        ]);
        // eslint-disable-next-line no-console
        console.log("[V2 community] fetchMemberPublications response:", response);
        if (response && response.status && Array.isArray(response.data)) {
          this.allPosts = response.data.map((publication) => {
            const normalized = { ...publication };
            if (normalized.type === "DOC" && !normalized.url && normalized.value) {
              normalized.url = normalized.value;
            }
            return normalized;
          });
        } else {
          this.allPosts = [];
        }
        this.allSections = sectionsResponse && sectionsResponse.status
          ? (sectionsResponse.data.sections || [])
          : [];
        this.allSurveys = surveysResponse && surveysResponse.status
          ? (surveysResponse.data.surveys || [])
          : [];
        if (this.activeSectionTab >= this.sectionTabs.length) this.activeSectionTab = 0;
        this.pageCount = 1;
      } catch (e) {
        this.allPosts = [];
        this.error = "Hubo un problema al obtener los comunicados. Verifica tu conexión.";
        this.show({
          text: "No se pudieron cargar los comunicados",
          color: "error",
        });
      } finally {
        this.loadingPosts = false;
      }
    },
    loadMore() {
      if (!this.hasMore) return;
      this.pageCount += 1;
    },
    setupObserver() {
      const sentinel = this.$refs.sentinel;
      if (!sentinel || typeof IntersectionObserver === "undefined") {
        return;
      }
      if (this.observer) {
        this.observer.disconnect();
      }
      this.observer = new IntersectionObserver(
        (entries) => {
          const entry = entries[0];
          if (entry && entry.isIntersecting && this.hasMore) {
            this.loadMore();
          }
        },
        { rootMargin: ROOT_MARGIN, threshold: 0 }
      );
      this.observer.observe(sentinel);
    },
  },
  async mounted() {
    this.loading();
    try {
      await this.loadPosts();
    } finally {
      this.loaded();
    }
  },
  beforeDestroy() {
    if (this.observer) {
      this.observer.disconnect();
    }
  },
};
</script>

<style scoped>
.v2-community {
  display: flex;
  flex-direction: column;
  gap: var(--space-6);
  max-width: 760px;
  margin-left: auto;
  margin-right: auto;
}

.v2-community__header {
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
}

.v2-community__title {
  margin: 0;
}

.v2-community__count {
  margin: var(--space-1) 0 0 0;
  font-size: var(--fs-body-sm);
}

.v2-community__sections {
  margin: calc(var(--space-2) * -1) 0;
}

.v2-community__composer {
  display: flex;
  align-items: center;
  gap: var(--space-3);
  padding: var(--space-3) var(--space-4);
  background-color: var(--color-surface);
  border: 1px dashed var(--color-border-strong);
  border-radius: var(--radius-xl);
  cursor: not-allowed;
  opacity: 0.85;
}

.v2-community__composer-avatar {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: var(--radius-full);
  background: var(--gradient-chamber);
  color: var(--color-navy-on);
  font-family: var(--font-headline);
  font-size: var(--fs-label-md);
  font-weight: var(--fw-semibold);
  flex-shrink: 0;
}

.v2-community__composer-body {
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex: 1 1 auto;
  min-width: 0;
}

.v2-community__composer-prompt {
  margin: 0;
  color: var(--color-text-muted);
  font-size: var(--fs-body-md);
}

.v2-community__composer-hint {
  margin: 0;
  font-size: var(--fs-body-sm);
  color: var(--color-text-subtle);
}

.v2-community__composer-badge {
  display: inline-flex;
  align-items: center;
  gap: var(--space-1);
  padding: var(--space-1) var(--space-3);
  font-size: var(--fs-label-xs);
  font-weight: var(--fw-semibold);
  text-transform: uppercase;
  letter-spacing: var(--tracking-wider);
  border-radius: var(--radius-full);
  background-color: var(--color-surface-low);
  color: var(--color-text-muted);
  white-space: nowrap;
}

.v2-community__loading,
.v2-community__empty,
.v2-community__error {
  padding: var(--space-8);
  background-color: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-lg);
  display: flex;
  flex-direction: column;
  align-items: center;
}

.v2-community__loading {
  gap: var(--space-2);
}

.v2-community__feed {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: var(--space-4);
}

.v2-community__feed-item {
  display: flex;
  min-width: 0;
}

.v2-community__feed-item > * {
  flex: 1 1 auto;
  width: 100%;
}

.v2-community__sentinel,
.v2-community__end {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--space-2);
  padding: var(--space-4);
}

.v2-community__end {
  color: var(--color-text-muted);
}
</style>
